// src/stores/PipelineStore.js - Zustand Store for Pipeline Management
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const usePipelineStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    // Main pipelines data
    pipelines: [],
    pipelinesLoading: false,

    // Selected pipeline state (for details view)
    selectedPipeline: null,
    selectedPipelineLoading: false,

    // Stages data (for selected pipeline)
    stages: [],
    stagesLoading: false,

    // Rules data (for selected stage)
    rules: [],
    rulesLoading: false,

    // Kanban board data (customers in pipeline)
    pipelineBoard: null, // { stages: [{id, name, items: [{customer, ...}]}] }
    boardLoading: false,

    // Automation flows data
    flows: [],
    flowsLoading: false,

    // Selected flow state (for canvas)
    selectedFlow: null,
    selectedFlowLoading: false,

    // Connection state
    isConnected: false,

    // Cache state
    cache: {
      pipelines: [],
      selectedPipeline: null,
      stages: [],
      rules: [],
      pipelineBoard: null,
      flows: [],
      selectedFlow: null,
      lastFetch: null
    },

    // Internal flags
    _initialized: false,

    // ================================
    // PIPELINE CRUD OPERATIONS
    // ================================

    /**
     * Request all pipelines from server
     */
    loadPipelines: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('🔄 PipelineStore: Loading pipelines');

      set({
        pipelinesLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_PIPELINES, {});
    },

    /**
     * Request detailed information for a specific pipeline
     * @param {string} pipelineId - Pipeline ID
     */
    loadPipelineDetails: (pipelineId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('📋 PipelineStore: Loading pipeline details:', pipelineId);

      set({
        selectedPipelineLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_PIPELINE, {
        pipeline_id: pipelineId
      });
    },

    /**
     * Request to create a new pipeline
     * @param {object} pipelineData - Pipeline information
     */
    createPipeline: (pipelineData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('➕ PipelineStore: Creating pipeline:', pipelineData.name);


      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_PIPELINE, pipelineData);
    },

    /**
     * Request to update an existing pipeline
     * @param {string} pipelineId - Pipeline ID
     * @param {object} updateData - Fields to update
     */
    updatePipeline: (pipelineId, updateData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('✏️ PipelineStore: Updating pipeline:', pipelineId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_PIPELINE, {
        pipeline_id: pipelineId,
        ...updateData
      });
    },

    /**
     * Request to delete a pipeline
     * @param {string} pipelineId - Pipeline ID
     */
    deletePipeline: (pipelineId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('🗑️ PipelineStore: Deleting pipeline:', pipelineId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_PIPELINE, {
        pipeline_id: pipelineId
      });
    },

    /**
     * Toggle pipeline active status
     * @param {string} pipelineId - Pipeline ID
     */
    togglePipelineStatus: (pipelineId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('🔄 PipelineStore: Toggling pipeline status:', pipelineId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.TOGGLE_PIPELINE_STATUS, {
        pipeline_id: pipelineId
      });
    },

    // ================================
    // STAGE MANAGEMENT OPERATIONS
    // ================================

    /**
     * Request stages for a pipeline
     * @param {string} pipelineId - Pipeline ID
     */
    loadStages: (pipelineId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('📊 PipelineStore: Loading stages for pipeline:', pipelineId);

      set({
        stagesLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_STAGES, {
        pipeline_id: pipelineId
      });
    },

    /**
     * Create a new stage in a pipeline
     * @param {string} pipelineId - Pipeline ID
     * @param {object} stageData - Stage information
     */
    createStage: (pipelineId, stageData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('➕ PipelineStore: Creating stage in pipeline:', pipelineId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_STAGE, {
        pipeline_id: pipelineId,
        ...stageData
      });
    },

    /**
     * Update an existing stage
     * @param {string} stageId - Stage ID
     * @param {object} updateData - Fields to update
     */
    updateStage: (stageId, updateData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('✏️ PipelineStore: Updating stage:', stageId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_STAGE, {
        stage_id: stageId,
        ...updateData
      });
    },

    /**
     * Delete a stage
     * @param {string} stageId - Stage ID
     */
    deleteStage: (stageId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('🗑️ PipelineStore: Deleting stage:', stageId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_STAGE, {
        stage_id: stageId
      });
    },

    /**
     * Reorder stages in a pipeline
     * @param {string} pipelineId - Pipeline ID
     * @param {array} stageOrder - Array of stage IDs in new order
     */
    reorderStages: (pipelineId, stageOrder) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('🔀 PipelineStore: Reordering stages in pipeline:', pipelineId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.REORDER_STAGES, {
        pipeline_id: pipelineId,
        stage_order: stageOrder
      });
    },

    // ================================
    // RULE MANAGEMENT OPERATIONS
    // ================================

    /**
     * Request rules for a stage
     * @param {string} stageId - Stage ID
     */
    loadRules: (stageId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('📜 PipelineStore: Loading rules for stage:', stageId);

      set({
        rulesLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_RULES, {
        stage_id: stageId
      });
    },

    /**
     * Create a new automation rule
     * @param {string} stageId - Stage ID
     * @param {object} ruleData - Rule information
     */
    createRule: (stageId, ruleData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('➕ PipelineStore: Creating rule for stage:', stageId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_RULE, {
        stage_id: stageId,
        ...ruleData
      });
    },

    /**
     * Update an existing rule
     * @param {string} ruleId - Rule ID
     * @param {object} updateData - Fields to update
     */
    updateRule: (ruleId, updateData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('✏️ PipelineStore: Updating rule:', ruleId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_RULE, {
        rule_id: ruleId,
        ...updateData
      });
    },

    /**
     * Delete a rule
     * @param {string} ruleId - Rule ID
     */
    deleteRule: (ruleId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('🗑️ PipelineStore: Deleting rule:', ruleId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_RULE, {
        rule_id: ruleId
      });
    },

    /**
     * Toggle rule active status
     * @param {string} ruleId - Rule ID
     */
    toggleRuleStatus: (ruleId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('🔄 PipelineStore: Toggling rule status:', ruleId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.TOGGLE_RULE_STATUS, {
        rule_id: ruleId
      });
    },

    // ================================
    // KANBAN/ITEM MANAGEMENT OPERATIONS
    // ================================

    /**
     * Request kanban board data for a pipeline
     * @param {string} pipelineId - Pipeline ID
     */
    loadPipelineBoard: (pipelineId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('📊 PipelineStore: Loading kanban board for pipeline:', pipelineId);

      set({
        boardLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_PIPELINE_BOARD, {
        pipeline_id: pipelineId
      });
    },

    /**
     * Add a customer to a pipeline
     * @param {string} pipelineId - Pipeline ID
     * @param {string} customerId - Customer ID
     * @param {string} stageId - Optional stage ID (defaults to first stage)
     */
    addCustomerToPipeline: (pipelineId, customerId, stageId = null) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('➕ PipelineStore: Adding customer to pipeline:', customerId, pipelineId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.ADD_CUSTOMER_TO_PIPELINE, {
        pipeline_id: pipelineId,
        customer_id: customerId,
        stage_id: stageId
      });
    },

    /**
     * Move a customer to a different stage
     * @param {string} itemId - Pipeline item ID
     * @param {string} newStageId - Target stage ID
     */
    moveCustomerToStage: (itemId, newStageId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('🔀 PipelineStore: Moving customer to stage:', itemId, newStageId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.MOVE_CUSTOMER_TO_STAGE, {
        item_id: itemId,
        new_stage_id: newStageId
      });
    },

    /**
     * Remove a customer from a pipeline
     * @param {string} itemId - Pipeline item ID
     */
    removeCustomerFromPipeline: (itemId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('➖ PipelineStore: Removing customer from pipeline:', itemId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.REMOVE_CUSTOMER_FROM_PIPELINE, {
        item_id: itemId
      });
    },

    /**
     * Get items (customers) for a specific stage
     * @param {string} stageId - Stage ID
     */
    loadStageItems: (stageId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('📋 PipelineStore: Loading items for stage:', stageId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_STAGE_ITEMS, {
        stage_id: stageId
      });
    },

    /**
     * Get customer's pipeline status
     * @param {string} pipelineId - Pipeline ID
     * @param {string} customerId - Customer ID
     */
    loadCustomerPipelineStatus: (pipelineId, customerId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('📋 PipelineStore: Loading customer pipeline status:', customerId, pipelineId);

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_CUSTOMER_PIPELINE_STATUS, {
        pipeline_id: pipelineId,
        customer_id: customerId
      });
    },

    // ================================
    // AUTOMATION FLOW CRUD OPERATIONS
    // ================================

    /**
     * Request all automation flows from server
     */
    loadFlows: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('🔄 PipelineStore: Loading automation flows');

      set({
        flowsLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_AUTOMATION_FLOWS, {});
    },

    /**
     * Request detailed information for a specific flow
     * @param {string} flowId - Flow ID
     */
    loadFlowDetails: (flowId) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('📋 PipelineStore: Loading flow details:', flowId);

      set({
        selectedFlowLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_AUTOMATION_FLOW, {
        flow_id: flowId
      });
    },

    /**
     * Create new automation flow
     * @param {object} flowData - Flow data (name, description, flow_config)
     */
    createFlow: (flowData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('➕ PipelineStore: Creating automation flow:', flowData.name);


      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_AUTOMATION_FLOW, flowData);
    },

    /**
     * Update existing automation flow
     * @param {string} flowId - Flow ID
     * @param {object} flowData - Updated flow data
     */
    updateFlow: (flowId, flowData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('✏️ PipelineStore: Updating automation flow:', flowId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_AUTOMATION_FLOW, {
        flow_id: flowId,
        ...flowData
      });
    },

    /**
     * Delete automation flow
     * @param {string} flowId - Flow ID
     */
    deleteFlow: (flowId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return;
      }

      console.log('🗑️ PipelineStore: Deleting automation flow:', flowId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_AUTOMATION_FLOW, {
        flow_id: flowId
      });
    },

    /**
     * Save flow configuration (nodes and edges)
     * @param {string} flowId - Flow ID
     * @param {object} flowConfig - Flow configuration {nodes: [], edges: []}
     */
    saveFlowConfig: (flowId, flowConfig) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PipelineStore: Socket service not initialized');
        return Promise.reject(new Error('Socket service not initialized'));
      }

      console.log('💾 PipelineStore: Saving flow config:', flowId);


      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_AUTOMATION_FLOW, {
        flow_id: flowId,
        flow_config: flowConfig
      });

      return Promise.resolve();
    },

    // ================================
    // UTILITY FUNCTIONS
    // ================================

    clearPipelinesError: () => {
    },

    clearSelectedPipeline: () => {
      set({
        selectedPipeline: null,
        stages: [],
        rules: []
      });
    },

    refreshPipelines: () => {
      // Clear cache first
      set(state => ({
        cache: {
          ...state.cache,
          pipelines: [],
          selectedPipeline: null,
          lastFetch: null
        }
      }));

      // Then reload
      get().loadPipelines();
    },

    getPipelineById: (pipelineId) => {
      const { pipelines } = get();
      return pipelines.find(p => p.id === pipelineId) || null;
    },

    getStageById: (stageId) => {
      const { stages } = get();
      return stages.find(s => s.id === stageId) || null;
    },

    getRuleById: (ruleId) => {
      const { rules } = get();
      return rules.find(r => r.id === ruleId) || null;
    },

    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================

    _setPipelinesLoaded: (data) => {
      // data can be either an array directly or an object with data property
      const pipelines = Array.isArray(data) ? data : (data.data || []);

      console.log('📡 PipelineStore: Pipelines received:', pipelines.length);

      set(state => ({
        pipelines,
        pipelinesLoading: false,
        cache: {
          ...state.cache,
          pipelines,
          lastFetch: new Date()
        }
      }));
    },

    _setPipelineDetailsLoaded: (pipelineData) => {
      console.log('📡 PipelineStore: Pipeline details received:', pipelineData.name);

      set(state => {
        // Add/update in main pipelines array
        const existingIndex = state.pipelines.findIndex(p => p.id === pipelineData.id);
        const updatedPipelines = existingIndex !== -1
          ? state.pipelines.map((p, index) =>
              index === existingIndex ? { ...p, ...pipelineData } : p
            )
          : [pipelineData, ...state.pipelines];

        return {
          pipelines: updatedPipelines,
          selectedPipeline: pipelineData,
          selectedPipelineLoading: false,
          stages: pipelineData.stages || [],
          cache: {
            ...state.cache,
            pipelines: updatedPipelines,
            selectedPipeline: pipelineData,
            stages: pipelineData.stages || []
          }
        };
      });
    },

    _addPipeline: (pipelineData) => {
      console.log('📡 PipelineStore: New pipeline created:', pipelineData.name);

      set(state => {
        // Check for duplicates
        const exists = state.pipelines.find(p => p.id === pipelineData.id);
        if (exists) return state;

        const updatedPipelines = [pipelineData, ...state.pipelines];

        return {
          pipelines: updatedPipelines,
            cache: {
            ...state.cache,
            pipelines: updatedPipelines
          }
        };
      });
    },

    _updatePipeline: (pipelineData) => {
      console.log('📡 PipelineStore: Pipeline updated:', pipelineData.name || pipelineData.id);

      set(state => {
        // Update in main pipelines array
        const updatedPipelines = state.pipelines.map(p =>
          p.id === pipelineData.id ? { ...p, ...pipelineData } : p
        );

        // Update selected pipeline if it's the same one
        const updatedSelectedPipeline = state.selectedPipeline?.id === pipelineData.id
          ? { ...state.selectedPipeline, ...pipelineData }
          : state.selectedPipeline;

        return {
          pipelines: updatedPipelines,
          selectedPipeline: updatedSelectedPipeline,
            cache: {
            ...state.cache,
            pipelines: updatedPipelines,
            selectedPipeline: updatedSelectedPipeline
          }
        };
      });
    },

    _removePipeline: (pipelineId) => {
      console.log('📡 PipelineStore: Pipeline deleted:', pipelineId);

      set(state => {
        // Remove from all caches
        const updatedPipelines = state.pipelines.filter(p => p.id !== pipelineId);

        // Clear selected pipeline if it was deleted
        const updatedSelectedPipeline = state.selectedPipeline?.id === pipelineId
          ? null
          : state.selectedPipeline;

        return {
          pipelines: updatedPipelines,
          selectedPipeline: updatedSelectedPipeline,
            cache: {
            ...state.cache,
            pipelines: updatedPipelines,
            selectedPipeline: updatedSelectedPipeline
          }
        };
      });
    },

    _setStagesLoaded: (data) => {
      // data can be either an array directly or an object with data property
      const stages = Array.isArray(data) ? data : (data.data || []);

      console.log('📡 PipelineStore: Stages received:', stages.length);

      set(state => ({
        stages,
        stagesLoading: false,
        cache: {
          ...state.cache,
          stages
        }
      }));
    },

    _addStage: (stageData) => {
      console.log('📡 PipelineStore: New stage created:', stageData.name);

      set(state => {
        const updatedStages = [...state.stages, stageData].sort((a, b) => a.position - b.position);

        return {
          stages: updatedStages,
            cache: {
            ...state.cache,
            stages: updatedStages
          }
        };
      });
    },

    _updateStage: (stageData) => {
      console.log('📡 PipelineStore: Stage updated:', stageData.name || stageData.id);

      set(state => {
        const updatedStages = state.stages.map(s =>
          s.id === stageData.id ? { ...s, ...stageData } : s
        ).sort((a, b) => a.position - b.position);

        return {
          stages: updatedStages,
            cache: {
            ...state.cache,
            stages: updatedStages
          }
        };
      });
    },

    _removeStage: (stageId) => {
      console.log('📡 PipelineStore: Stage deleted:', stageId);

      set(state => {
        const updatedStages = state.stages.filter(s => s.id !== stageId);

        return {
          stages: updatedStages,
            cache: {
            ...state.cache,
            stages: updatedStages
          }
        };
      });
    },

    _setRulesLoaded: (data) => {
      // data can be either an array directly or an object with data property
      const rules = Array.isArray(data) ? data : (data.data || []);

      console.log('📡 PipelineStore: Rules received:', rules.length);

      set(state => ({
        rules,
        rulesLoading: false,
        cache: {
          ...state.cache,
          rules
        }
      }));
    },

    _addRule: (ruleData) => {
      console.log('📡 PipelineStore: New rule created:', ruleData.name);

      set(state => {
        const updatedRules = [...state.rules, ruleData];

        return {
          rules: updatedRules,
            cache: {
            ...state.cache,
            rules: updatedRules
          }
        };
      });
    },

    _updateRule: (ruleData) => {
      console.log('📡 PipelineStore: Rule updated:', ruleData.name || ruleData.id);

      set(state => {
        const updatedRules = state.rules.map(r =>
          r.id === ruleData.id ? { ...r, ...ruleData } : r
        );

        return {
          rules: updatedRules,
            cache: {
            ...state.cache,
            rules: updatedRules
          }
        };
      });
    },

    _removeRule: (ruleId) => {
      console.log('📡 PipelineStore: Rule deleted:', ruleId);

      set(state => {
        const updatedRules = state.rules.filter(r => r.id !== ruleId);

        return {
          rules: updatedRules,
            cache: {
            ...state.cache,
            rules: updatedRules
          }
        };
      });
    },

    _setBoardLoaded: (boardData) => {
      console.log('📡 PipelineStore: Board data received');

      set(state => ({
        pipelineBoard: boardData,
        boardLoading: false,
        cache: {
          ...state.cache,
          pipelineBoard: boardData
        }
      }));
    },

    _updateBoardItem: (itemData) => {
      console.log('📡 PipelineStore: Board item updated');

      set(state => {
        if (!state.pipelineBoard) return state;

        // Update the board data with the new item position
        // This is handled by re-fetching the board in real implementation
        return {
          };
      });
    },

    // ================================
    // INTERNAL FLOW HANDLERS
    // ================================

    _setFlowsLoaded: (data) => {
      // data can be either an array directly or an object with data property
      const flows = Array.isArray(data) ? data : (data.data || []);

      console.log('📡 PipelineStore: Automation flows received:', flows.length);

      set(state => ({
        flows,
        flowsLoading: false,
        cache: {
          ...state.cache,
          flows,
          lastFetch: new Date()
        }
      }));
    },

    _setFlowDetailsLoaded: (flowData) => {
      console.log('📡 PipelineStore: Flow details received:', flowData.name);

      set(state => {
        // Add/update in main flows array
        const existingIndex = state.flows.findIndex(f => f.id === flowData.id);
        const updatedFlows = existingIndex !== -1
          ? state.flows.map((f, index) =>
              index === existingIndex ? { ...f, ...flowData } : f
            )
          : [flowData, ...state.flows];

        return {
          flows: updatedFlows,
          selectedFlow: flowData,
          selectedFlowLoading: false,
          cache: {
            ...state.cache,
            flows: updatedFlows,
            selectedFlow: flowData
          }
        };
      });
    },

    _addFlow: (flowData) => {
      console.log('📡 PipelineStore: New flow created:', flowData.name);

      set(state => {
        // Check for duplicates
        const exists = state.flows.find(f => f.id === flowData.id);
        if (exists) return state;

        const updatedFlows = [flowData, ...state.flows];

        return {
          flows: updatedFlows,
          cache: {
            ...state.cache,
            flows: updatedFlows
          }
        };
      });
    },

    _updateFlow: (flowData) => {
      console.log('📡 PipelineStore: Flow updated:', flowData.name || flowData.id);

      set(state => {
        // Update in main flows array
        const updatedFlows = state.flows.map(f =>
          f.id === flowData.id ? { ...f, ...flowData } : f
        );

        // Update selected flow if it's the same one
        const updatedSelectedFlow = state.selectedFlow?.id === flowData.id
          ? { ...state.selectedFlow, ...flowData }
          : state.selectedFlow;

        return {
          flows: updatedFlows,
          selectedFlow: updatedSelectedFlow,
          cache: {
            ...state.cache,
            flows: updatedFlows,
            selectedFlow: updatedSelectedFlow
          }
        };
      });
    },

    _removeFlow: (flowId) => {
      console.log('📡 PipelineStore: Flow deleted:', flowId);

      set(state => {
        const updatedFlows = state.flows.filter(f => f.id !== flowId);

        // Clear selected flow if it was deleted
        const updatedSelectedFlow = state.selectedFlow?.id === flowId
          ? null
          : state.selectedFlow;

        return {
          flows: updatedFlows,
          selectedFlow: updatedSelectedFlow,
          cache: {
            ...state.cache,
            flows: updatedFlows,
            selectedFlow: updatedSelectedFlow
          }
        };
      });
    },

    // ================================
    // INTERNAL METHODS (socket manager, initialization)
    // ================================

    _getSocketManager: () => {
      // Access socket manager from window global
      return {
        socketManager: typeof window !== 'undefined' ? window.__socketManager : null
      };
    },

    _initialize: (socketManager) => {
      const state = get();

      console.log('🔌 PipelineStore: Initializing with socket manager');

      // Store socket manager reference
      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }

      set({
        _initialized: true,
        isConnected: socketManager?.isAuthenticated || false
      });

      // Auto-load pipelines and flows if authenticated (like CustomerStore pattern)
      if (socketManager?.isAuthenticated) {
        console.log('🔄 PipelineStore: Auto-loading pipelines and flows (first time)');
        state.loadPipelines();
        state.loadFlows();
      }
    },

    _setConnectionState: (connected) => {
      set({ isConnected: connected });
    },

    _handleAuthReset: () => {
      console.log('🧹 PipelineStore: Resetting all state due to auth reset');

      set({
        // Reset all state to initial values
        pipelines: [],
        pipelinesLoading: false,
        selectedPipeline: null,
        selectedPipelineLoading: false,
        stages: [],
        stagesLoading: false,
        rules: [],
        rulesLoading: false,
        pipelineBoard: null,
        boardLoading: false,
        flows: [],
        flowsLoading: false,
        selectedFlow: null,
        selectedFlowLoading: false,
        isConnected: false,
        cache: {
          pipelines: [],
          selectedPipeline: null,
          stages: [],
          rules: [],
          pipelineBoard: null,
          flows: [],
          selectedFlow: null,
          lastFetch: null
        },
        _initialized: false
      });
    }
  }))
);

// ================================
// SOCKET EVENT LISTENERS SETUP (Global initialization)
// ================================

export const initializePipelineStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }

  console.log('👂 PipelineStore: Setting up socket event listeners');

  const { universalHandler } = socketManager;

  // Set up universal response listener for all pipeline events
  const handleSocketResponse = (responseData) => {
    const eventName = responseData.event;
    const store = usePipelineStore.getState();

    switch (eventName) {
      // ✅ PIPELINE SUCCESS EVENTS
      case SOCKET_EVENTS.PIPELINES_LIST:
        store._setPipelinesLoaded(responseData.data || responseData);
        break;

      case SOCKET_EVENTS.PIPELINE_DETAILS:
        store._setPipelineDetailsLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.PIPELINE_CREATED:
        store._addPipeline(responseData.data);
        break;

      case SOCKET_EVENTS.PIPELINE_UPDATED:
      case SOCKET_EVENTS.PIPELINE_STATUS_TOGGLED:
        store._updatePipeline(responseData.data);
        break;

      case SOCKET_EVENTS.PIPELINE_DELETED:
        store._removePipeline(responseData.data.pipeline_id || responseData.data.id);
        break;

      // ✅ STAGE SUCCESS EVENTS
      case SOCKET_EVENTS.STAGES_LIST:
        store._setStagesLoaded(responseData.data || responseData);
        break;

      case SOCKET_EVENTS.STAGE_CREATED:
        store._addStage(responseData.data);
        break;

      case SOCKET_EVENTS.STAGE_UPDATED:
      case SOCKET_EVENTS.STAGES_REORDERED:
        // For reorder, reload all stages
        if (eventName === SOCKET_EVENTS.STAGES_REORDERED && responseData.data.stages) {
          store._setStagesLoaded({ data: responseData.data.stages });
        } else {
          store._updateStage(responseData.data);
        }
        break;

      case SOCKET_EVENTS.STAGE_DELETED:
        store._removeStage(responseData.data.stage_id || responseData.data.id);
        break;

      // ✅ RULE SUCCESS EVENTS
      case SOCKET_EVENTS.RULES_LIST:
        store._setRulesLoaded(responseData.data || responseData);
        break;

      case SOCKET_EVENTS.RULE_CREATED:
        store._addRule(responseData.data);
        break;

      case SOCKET_EVENTS.RULE_UPDATED:
      case SOCKET_EVENTS.RULE_STATUS_TOGGLED:
        store._updateRule(responseData.data);
        break;

      case SOCKET_EVENTS.RULE_DELETED:
        store._removeRule(responseData.data.rule_id || responseData.data.id);
        break;

      // ✅ BOARD/ITEM SUCCESS EVENTS
      case SOCKET_EVENTS.PIPELINE_BOARD_DATA:
        store._setBoardLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.CUSTOMER_ADDED_TO_PIPELINE:
      case SOCKET_EVENTS.CUSTOMER_MOVED_TO_STAGE:
      case SOCKET_EVENTS.CUSTOMER_REMOVED_FROM_PIPELINE:
        store._updateBoardItem(responseData.data);
        // Reload board data to get fresh state
        if (responseData.data.pipeline_id) {
          store.loadPipelineBoard(responseData.data.pipeline_id);
        }
        break;

      case SOCKET_EVENTS.STAGE_ITEMS_LIST:
        // Handle stage items if needed
        console.log('📡 PipelineStore: Stage items received');
        break;

      case SOCKET_EVENTS.CUSTOMER_PIPELINE_STATUS:
        // Handle customer pipeline status if needed
        console.log('📡 PipelineStore: Customer pipeline status received');
        break;

      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_PIPELINES_FAILED:
        set({ pipelinesLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_PIPELINE_FAILED:
      case SOCKET_EVENTS.UPDATE_PIPELINE_FAILED:
      case SOCKET_EVENTS.DELETE_PIPELINE_FAILED:
      case SOCKET_EVENTS.TOGGLE_PIPELINE_STATUS_FAILED:
        console.log('❌ PipelineStore: Pipeline operation failed -', responseData.message);
        break;

      case SOCKET_EVENTS.GET_PIPELINE_FAILED:
        set({ selectedPipelineLoading: false });
        break;

      case SOCKET_EVENTS.GET_STAGES_FAILED:
        set({ stagesLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_STAGE_FAILED:
      case SOCKET_EVENTS.UPDATE_STAGE_FAILED:
      case SOCKET_EVENTS.DELETE_STAGE_FAILED:
      case SOCKET_EVENTS.REORDER_STAGES_FAILED:
        console.log('❌ PipelineStore: Stage operation failed -', responseData.message);
        break;

      case SOCKET_EVENTS.GET_RULES_FAILED:
        set({ rulesLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_RULE_FAILED:
      case SOCKET_EVENTS.UPDATE_RULE_FAILED:
      case SOCKET_EVENTS.DELETE_RULE_FAILED:
      case SOCKET_EVENTS.TOGGLE_RULE_STATUS_FAILED:
        console.log('❌ PipelineStore: Rule operation failed -', responseData.message);
        break;

      case SOCKET_EVENTS.GET_PIPELINE_BOARD_FAILED:
        set({ boardLoading: false });
        break;

      case SOCKET_EVENTS.ADD_CUSTOMER_TO_PIPELINE_FAILED:
      case SOCKET_EVENTS.MOVE_CUSTOMER_TO_STAGE_FAILED:
      case SOCKET_EVENTS.REMOVE_CUSTOMER_FROM_PIPELINE_FAILED:
        console.log('❌ PipelineStore: Board operation failed -', responseData.message);
        break;

      // ✅ AUTOMATION FLOW SUCCESS EVENTS
      case SOCKET_EVENTS.AUTOMATION_FLOWS_LIST:
        store._setFlowsLoaded(responseData.data || responseData);
        break;

      case SOCKET_EVENTS.AUTOMATION_FLOW_DETAILS:
        store._setFlowDetailsLoaded(responseData.data || responseData);
        break;

      case SOCKET_EVENTS.AUTOMATION_FLOW_CREATED:
        store._addFlow(responseData.data || responseData);
        break;

      case SOCKET_EVENTS.AUTOMATION_FLOW_UPDATED:
        store._updateFlow(responseData.data || responseData);
        break;

      case SOCKET_EVENTS.AUTOMATION_FLOW_DELETED:
        store._removeFlow(responseData.flow_id || responseData.id);
        break;

      // ❌ AUTOMATION FLOW ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_AUTOMATION_FLOWS_FAILED:
        set({ flowsLoading: false });
        break;

      case SOCKET_EVENTS.GET_AUTOMATION_FLOW_FAILED:
        set({ selectedFlowLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_AUTOMATION_FLOW_FAILED:
      case SOCKET_EVENTS.UPDATE_AUTOMATION_FLOW_FAILED:
      case SOCKET_EVENTS.DELETE_AUTOMATION_FLOW_FAILED:
        console.log('❌ PipelineStore: Flow operation failed -', responseData.message);
        break;

      default:
        // Not a pipeline-related event, ignore
        break;
    }
  };

  // Set up individual event listeners using existing system
  const pipelineEvents = [
    // Pipeline events
    SOCKET_EVENTS.PIPELINES_LIST,
    SOCKET_EVENTS.PIPELINE_DETAILS,
    SOCKET_EVENTS.PIPELINE_CREATED,
    SOCKET_EVENTS.PIPELINE_UPDATED,
    SOCKET_EVENTS.PIPELINE_DELETED,
    SOCKET_EVENTS.PIPELINE_STATUS_TOGGLED,
    SOCKET_EVENTS.GET_PIPELINES_FAILED,
    SOCKET_EVENTS.GET_PIPELINE_FAILED,
    SOCKET_EVENTS.CREATE_PIPELINE_FAILED,
    SOCKET_EVENTS.UPDATE_PIPELINE_FAILED,
    SOCKET_EVENTS.DELETE_PIPELINE_FAILED,
    SOCKET_EVENTS.TOGGLE_PIPELINE_STATUS_FAILED,
    // Stage events
    SOCKET_EVENTS.STAGES_LIST,
    SOCKET_EVENTS.STAGE_CREATED,
    SOCKET_EVENTS.STAGE_UPDATED,
    SOCKET_EVENTS.STAGE_DELETED,
    SOCKET_EVENTS.STAGES_REORDERED,
    SOCKET_EVENTS.GET_STAGES_FAILED,
    SOCKET_EVENTS.CREATE_STAGE_FAILED,
    SOCKET_EVENTS.UPDATE_STAGE_FAILED,
    SOCKET_EVENTS.DELETE_STAGE_FAILED,
    SOCKET_EVENTS.REORDER_STAGES_FAILED,
    // Rule events
    SOCKET_EVENTS.RULES_LIST,
    SOCKET_EVENTS.RULE_CREATED,
    SOCKET_EVENTS.RULE_UPDATED,
    SOCKET_EVENTS.RULE_DELETED,
    SOCKET_EVENTS.RULE_STATUS_TOGGLED,
    SOCKET_EVENTS.GET_RULES_FAILED,
    SOCKET_EVENTS.CREATE_RULE_FAILED,
    SOCKET_EVENTS.UPDATE_RULE_FAILED,
    SOCKET_EVENTS.DELETE_RULE_FAILED,
    SOCKET_EVENTS.TOGGLE_RULE_STATUS_FAILED,
    // Board/Item events
    SOCKET_EVENTS.PIPELINE_BOARD_DATA,
    SOCKET_EVENTS.CUSTOMER_ADDED_TO_PIPELINE,
    SOCKET_EVENTS.CUSTOMER_MOVED_TO_STAGE,
    SOCKET_EVENTS.CUSTOMER_REMOVED_FROM_PIPELINE,
    SOCKET_EVENTS.STAGE_ITEMS_LIST,
    SOCKET_EVENTS.CUSTOMER_PIPELINE_STATUS,
    SOCKET_EVENTS.GET_PIPELINE_BOARD_FAILED,
    SOCKET_EVENTS.ADD_CUSTOMER_TO_PIPELINE_FAILED,
    SOCKET_EVENTS.MOVE_CUSTOMER_TO_STAGE_FAILED,
    SOCKET_EVENTS.REMOVE_CUSTOMER_FROM_PIPELINE_FAILED,
    // Flow events
    SOCKET_EVENTS.AUTOMATION_FLOWS_LIST,
    SOCKET_EVENTS.AUTOMATION_FLOW_DETAILS,
    SOCKET_EVENTS.AUTOMATION_FLOW_CREATED,
    SOCKET_EVENTS.AUTOMATION_FLOW_UPDATED,
    SOCKET_EVENTS.AUTOMATION_FLOW_DELETED,
    SOCKET_EVENTS.GET_AUTOMATION_FLOWS_FAILED,
    SOCKET_EVENTS.GET_AUTOMATION_FLOW_FAILED,
    SOCKET_EVENTS.CREATE_AUTOMATION_FLOW_FAILED,
    SOCKET_EVENTS.UPDATE_AUTOMATION_FLOW_FAILED,
    SOCKET_EVENTS.DELETE_AUTOMATION_FLOW_FAILED
  ];

  // Set up individual event listeners using existing system
  pipelineEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      handleSocketResponse({ event: eventName, data });
    });
  });

  // Set up auth reset listener
  universalHandler.on('auth_reset', () => {
    const store = usePipelineStore.getState();
    store._handleAuthReset();
  });

  // Initialize the store (like CustomerStore pattern)
  usePipelineStore.getState()._initialize(socketManager);

  console.log('✅ PipelineStore: Socket event listeners registered');
};

// Legacy function - kept for backward compatibility
// StoreInitializer now calls initializePipelineStoreSocketListeners directly
export const initializePipelineStore = (socketManager) => {
  initializePipelineStoreSocketListeners(socketManager);
};

export default usePipelineStore;
